import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SectionHeader } from '../components/SectionHeader';
import { Button } from '../components/Button';
import { GlowOrb } from '../components/GlowOrb';
import { services } from '../data/services';
import { Shield, Heart, Calendar, CheckCircle2 } from 'lucide-react';
export function Booking() {
  const location = useLocation();
  const [selectedService, setSelectedService] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceSlug = params.get('service');
    if (serviceSlug) {
      setSelectedService(serviceSlug);
    }
  }, [location]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (isSubmitted) {
    return <div className="min-h-[80vh] flex items-center justify-center relative py-24" data-id="element-406">
        <GlowOrb color="gold" size="lg" position="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-407" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-xl mx-auto glass p-12 md:p-16 rounded-[3rem] text-center relative z-10" data-id="element-408">
          <div className="w-24 h-24 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-409">
            <CheckCircle2 className="w-12 h-12 text-gold" data-id="element-410" />
          </div>
          <h2 className="text-4xl font-serif text-white mb-4" data-id="element-411">
            Request Received
          </h2>
          <p className="text-beige-muted text-lg mb-8" data-id="element-412">
            Your healing session request has been submitted. We will send a
            confirmation email with your secure payment link shortly.
          </p>
          <Button href="/" variant="primary" data-id="element-413">
            Return Home
          </Button>
        </motion.div>
      </div>;
  }
  return <div className="pt-32 pb-24 relative overflow-hidden" data-id="element-414">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{
      backgroundImage: `url(https://images.unsplash.com/photo-1602007900119-bdc6e5b54e6e?q=80&w=2000&auto=format&fit=crop)`
    }} data-id="element-415"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/85 to-midnight" data-id="element-416"></div>
      <GlowOrb color="primary" size="xl" position="-top-20 -left-40" data-id="element-417" />
      <GlowOrb color="gold" size="lg" position="bottom-0 right-0" delay={2} data-id="element-418" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-419">
        <SectionHeader eyebrow="Reserve Your Space" title="Book Your Healing Session" description="Take the first step toward energetic alignment and inner peace. All sessions are conducted via secure video call." data-id="element-420" />

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16" data-id="element-421">
          {/* Booking Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="lg:col-span-7 glass p-8 md:p-12 rounded-[2.5rem]" data-id="element-422">
            <form className="space-y-8" onSubmit={handleSubmit} data-id="element-423">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-424">
                <div className="space-y-3" data-id="element-425">
                  <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-426">
                    Full Name
                  </label>
                  <input required type="text" className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white placeholder:text-white/20 transition-all" placeholder="Your name" data-id="element-427" />
                </div>
                <div className="space-y-3" data-id="element-428">
                  <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-429">
                    Email Address
                  </label>
                  <input required type="email" className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white placeholder:text-white/20 transition-all" placeholder="your@email.com" data-id="element-430" />
                </div>
              </div>

              <div className="space-y-3" data-id="element-431">
                <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-432">
                  Select Service
                </label>
                <div className="relative" data-id="element-433">
                  <select required value={selectedService} onChange={e => setSelectedService(e.target.value)} className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white appearance-none transition-all" data-id="element-434">
                    <option value="" disabled data-id="element-435">
                      Choose a healing service...
                    </option>
                    {services.map(s => <option key={s.id} value={s.slug} data-id="element-436">
                        {s.title} - ${s.price}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-5 flex items-center pointer-events-none" data-id="element-437">
                    <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-438">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-439"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-440">
                <div className="space-y-3" data-id="element-441">
                  <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-442">
                    Preferred Date
                  </label>
                  <input required type="date" className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white transition-all [color-scheme:dark]" data-id="element-443" />
                </div>
                <div className="space-y-3" data-id="element-444">
                  <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-445">
                    Preferred Time
                  </label>
                  <div className="relative" data-id="element-446">
                    <select required className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white appearance-none transition-all" data-id="element-447">
                      <option value="" disabled selected data-id="element-448">
                        Select time...
                      </option>
                      <option data-id="element-449">Morning (9AM - 12PM)</option>
                      <option data-id="element-450">Afternoon (12PM - 4PM)</option>
                      <option data-id="element-451">Evening (4PM - 8PM)</option>
                    </select>
                    <div className="absolute inset-y-0 right-5 flex items-center pointer-events-none" data-id="element-452">
                      <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-453">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-454"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3" data-id="element-455">
                <label className="text-sm font-medium text-beige uppercase tracking-wider" data-id="element-456">
                  Special Notes or Intentions
                </label>
                <textarea rows={4} className="w-full px-5 py-4 rounded-xl bg-midnight/50 border border-white/10 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-white placeholder:text-white/20 transition-all resize-none" placeholder="What brings you to healing today?" data-id="element-457"></textarea>
              </div>

              <Button type="submit" fullWidth size="lg" variant="primary" className="mt-4" data-id="element-458">
                Confirm Booking Request
              </Button>
              <p className="text-xs text-center text-beige-muted mt-4" data-id="element-459">
                Payment will be collected securely after confirming your time
                slot.
              </p>
            </form>
          </motion.div>

          {/* Sidebar Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="lg:col-span-5 space-y-8" data-id="element-460">
            <div className="bg-gradient-to-br from-primary to-midnight-panel p-8 md:p-10 rounded-[2.5rem] border border-white/10 relative overflow-hidden" data-id="element-461">
              <div className="absolute inset-0 bg-star-dust opacity-30 mix-blend-screen" data-id="element-462"></div>
              <h3 className="text-2xl font-serif text-white mb-8 relative z-10" data-id="element-463">
                What Happens Next?
              </h3>
              <ul className="space-y-8 relative z-10" data-id="element-464">
                <li className="flex gap-5" data-id="element-465">
                  <div className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center flex-shrink-0 font-bold border border-gold/30" data-id="element-466">
                    1
                  </div>
                  <div data-id="element-467">
                    <h4 className="font-serif text-lg text-white mb-1" data-id="element-468">
                      Confirmation
                    </h4>
                    <p className="text-sm text-beige-muted leading-relaxed" data-id="element-469">
                      You'll receive an email confirming your time slot and
                      payment link.
                    </p>
                  </div>
                </li>
                <li className="flex gap-5" data-id="element-470">
                  <div className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center flex-shrink-0 font-bold border border-gold/30" data-id="element-471">
                    2
                  </div>
                  <div data-id="element-472">
                    <h4 className="font-serif text-lg text-white mb-1" data-id="element-473">
                      Preparation
                    </h4>
                    <p className="text-sm text-beige-muted leading-relaxed" data-id="element-474">
                      24 hours before, we'll send a reminder with your secure
                      video link.
                    </p>
                  </div>
                </li>
                <li className="flex gap-5" data-id="element-475">
                  <div className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center flex-shrink-0 font-bold border border-gold/30" data-id="element-476">
                    3
                  </div>
                  <div data-id="element-477">
                    <h4 className="font-serif text-lg text-white mb-1" data-id="element-478">
                      Healing
                    </h4>
                    <p className="text-sm text-beige-muted leading-relaxed" data-id="element-479">
                      Log in 5 minutes early, relax, and prepare to receive
                      healing.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-4" data-id="element-480">
              <div className="flex items-center gap-5 p-6 glass rounded-2xl" data-id="element-481">
                <Shield className="w-8 h-8 text-gold" data-id="element-482" />
                <div data-id="element-483">
                  <h4 className="font-serif text-lg text-white" data-id="element-484">Safe Space</h4>
                  <p className="text-sm text-beige-muted" data-id="element-485">
                    100% confidential and judgment-free.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-5 p-6 glass rounded-2xl" data-id="element-486">
                <Heart className="w-8 h-8 text-gold" data-id="element-487" />
                <div data-id="element-488">
                  <h4 className="font-serif text-lg text-white" data-id="element-489">
                    Compassionate
                  </h4>
                  <p className="text-sm text-beige-muted" data-id="element-490">
                    Tailored to your unique emotional needs.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-5 p-6 glass rounded-2xl" data-id="element-491">
                <Calendar className="w-8 h-8 text-gold" data-id="element-492" />
                <div data-id="element-493">
                  <h4 className="font-serif text-lg text-white" data-id="element-494">Flexible</h4>
                  <p className="text-sm text-beige-muted" data-id="element-495">
                    Easy rescheduling up to 24h before.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}